import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import useCasesStore from '../stores/casesStore';
import CurrencyIcon from './CurrencyIcon';
import useUserStore from '../stores/userStore';
import { formatPrice } from '../utils/formatPrice';
import { useTranslation, Trans } from 'react-i18next';
import config from '../config';

const CaseContentsView = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const { cases, loading, fetchCases } = useCasesStore();
  const [caseData, setCaseData] = useState(null);
  const theme = useTheme();
  const user = useUserStore(state => state.user);
  const balance = user?.balance ? parseFloat(user.balance) : 0;
  const { t } = useTranslation();

  useEffect(() => {
    if (!cases.length) fetchCases();
  }, [cases.length, fetchCases]);

  useEffect(() => {
    if (cases.length) {
      const found = cases.find(c => String(c.id) === String(caseId));
      setCaseData(found);
    }
  }, [cases, caseId]);

  useEffect(() => {
    if (window.Telegram?.WebApp?.BackButton) {
      window.Telegram.WebApp.BackButton.show();
      window.Telegram.WebApp.BackButton.onClick(() => {
        navigate('/cases', { replace: true });
      });
    }
    return () => {
      if (window.Telegram?.WebApp?.BackButton) {
        window.Telegram.WebApp.BackButton.hide();
        window.Telegram.WebApp.BackButton.offClick();
      }
    };
  }, [navigate]);

  if (loading || !caseData) {
    return <Container><LoadingText>Загрузка...</LoadingText></Container>;
  }

  const notEnoughTokens = balance < parseFloat(caseData.tokens);

  return (
    <Container>
      <HeaderBlock>
        <CaseImageWrapper>
          <CaseImage src={config.getImageUrl(caseData.image)} alt={caseData.name} onError={e => e.target.src = '/placeholder-case.png'} />
        </CaseImageWrapper>
        <OpenButtonTop
          onClick={() => {
            if (window.Telegram?.WebApp?.HapticFeedback?.impactOccurred) {
              window.Telegram.WebApp.HapticFeedback.impactOccurred('medium');
            }
            navigate(`/case/${caseId}`, { state: { autoSpin: true } });
          }}
          disabled={notEnoughTokens}
          style={notEnoughTokens ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
        >
          <Trans
            i18nKey="case.openCase"
            components={{ currency: <CurrencyIcon size={22} /> }}
            values={{ price: formatPrice(caseData.tokens) }}
          />
        </OpenButtonTop>
        <CaseDesc>{caseData.description}</CaseDesc>
      </HeaderBlock>
      <SectionTitle>{t('case.itemsInCase', 'Предметы в кейсе')}</SectionTitle>
      <ItemsGrid>
        {caseData.items && caseData.items.length > 0 ? caseData.items.map(item => {
          // Вытаскиваем wear (например, (Field-Tested))
          let name = item.name;
          let wear = '';
          const wearMatch = name.match(/\(([^)]+)\)$/);
          if (wearMatch) {
            wear = wearMatch[0];
            name = name.replace(wear, '').trim();
          }
          const parts = name.split(' | ');
          return (
            <ItemCard key={item.id} chance={item.chance}>
              <ChanceBadge>{(() => {
                const raw = item.chance.toString();
                const [intPart, fracPart] = raw.split('.');
                if (!fracPart) return Number(item.chance).toFixed(0) + '%';
                let digits = 2;
                let formatted = Number(item.chance).toFixed(digits);
                while (formatted === '0.00' && digits < fracPart.length) {
                  digits++;
                  formatted = Number(item.chance).toFixed(digits);
                }
                // Если всё равно нули, выводим с максимальной точностью
                if (/^0\.0+$/.test(formatted)) {
                  formatted = Number(item.chance).toFixed(fracPart.length);
                }
                // Убираем лишние нули справа, если дробная часть стала длинной
                formatted = formatted.replace(/(\.[0-9]*[1-9])0+$/, '$1');
                return formatted + '%';
              })()}</ChanceBadge>
              <ItemIcon src={item.iconUrl} alt={item.name} onError={e => e.target.src = '/placeholder-case.png'} />
              <ItemName>
                <span style={{ fontWeight: 700, color: '#fff', display: 'block' }}>{parts[0]}</span>
                {parts.slice(1).map((part, idx) => (
                  <span key={idx} style={{ color: '#bdbdbd', display: 'block' }}>{part}</span>
                ))}
                {wear && <span style={{ color: '#bdbdbd', display: 'block' }}>{wear}</span>}
              </ItemName>
              {/* Цена без иконки */}
              <ItemPrice>{item.price}</ItemPrice>
            </ItemCard>
          );
        }) : <LoadingText>Нет предметов</LoadingText>}
      </ItemsGrid>
    </Container>
  );
};

const Container = styled.div`
  min-height: 100vh;
  background: ${props => props.theme.background};
  padding: 0 16px 32px 16px;
`;

const HeaderBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  ${'' /* margin-bottom: 20px; */}
  margin-top: 80px;
`;

const CaseImageWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 440px;
  min-height: 220px;
  margin: 0 auto 28px auto;
  border-radius: 32px;
  box-shadow: none;
`;

const CaseImage = styled.img`
  width: 90%;
  max-width: 400px;
  height: auto;
  object-fit: contain;
  margin: 0 auto;
  display: block;
  filter: drop-shadow(0 4px 24px rgba(0,0,0,0.18));
`;

const CaseName = styled.h1`
  font-size: 32px;
  font-weight: 800;
  color: ${props => props.theme.text.primary};
  margin: 0 0 8px 0;
  text-align: center;
`;

const CaseDesc = styled.div`
  font-size: 11px;
  color: ${props => props.theme.text.secondary};
  text-align: center;
  margin-bottom: 8px;
`;

const SectionTitle = styled.div`
  font-size: 22px;
  font-weight: 800;
  color: ${props => props.theme.text.primary};
  margin-bottom: 16px;
  text-align: left;
`;

const ItemsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 18px;
  margin-bottom: 32px;

  @media (max-width: 600px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
  }

  @media (min-width: 601px) and (max-width: 900px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
  }

  @media (min-width: 901px) {
    grid-template-columns: repeat(5, 1fr);
    gap: 20px;
  }
`;

const ItemCard = styled.div`
  background: ${props => props.theme.card.background};
  border-radius: 18px;
  padding: 20px 8px 16px 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  box-shadow: 0 2px 12px rgba(0,0,0,0.10);
  border-bottom: ${props => {
    const chance = parseFloat(props.chance);
    if (chance <= 0.1) return '2px solid #ff6b35';
    if (chance <= 0.5) return '2px solid #ffd700';
    if (chance <= 2) return '2px solid #4ade80';
    if (chance <= 10) return '2px solid #3b82f6';
    return '2px solid #6b7280';
  }};

  @media (max-width: 600px) {
    padding: 16px 6px 12px 6px;
    border-radius: 14px;
  }
`;

const ChanceBadge = styled.div`
  position: absolute;
  top: 10px;
  right: 14px;
  background: #23272f;
  color: #facc15;
  font-size: 11px;
  font-weight: 700;
  border-radius: 6px;
  padding: 1px 6px;
  z-index: 2;

  @media (max-width: 600px) {
    top: 8px;
    right: 10px;
    font-size: 10px;
    padding: 1px 4px;
  }
`;

const ItemIcon = styled.img`
  width: 96px;
  height: 96px;
  object-fit: contain;
  margin-bottom: 12px;

  @media (max-width: 600px) {
    width: 80px;
    height: 80px;
    margin-bottom: 8px;
  }
`;

const ItemName = styled.div`
  font-size: 15px;
  color: ${props => props.theme.text.primary};
  text-align: center;
  margin-bottom: 4px;
  font-weight: 600;

  @media (max-width: 600px) {
    font-size: 13px;
  }
`;

const ItemPrice = styled.div`
  font-size: 15px;
  color: #22c55e;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const OpenButton = styled.button`
  width: 100%;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: ${props => props.theme.primary};
  color: #fff;
  border: none;
  border-radius: 16px;
  font-size: 20px;
  font-weight: 800;
  padding: 18px 0;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(37,99,235,0.10);
  transition: background 0.2s;
  margin-top: 32px;
  &:hover {
    background: #2563eb;
  }
`;

const OpenButtonTop = styled.button`
  width: 100%;
  max-width: 340px;
  margin: 0 auto 32px auto;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  background: #2563eb;
  color: #fff;
  border: none;
  border-radius: 16px;
  font-size: 18px;
  font-weight: 900;
  padding: 16px 0;
  cursor: pointer;
  box-shadow: 0 4px 24px rgba(37,99,235,0.18);
  transition: background 0.2s, box-shadow 0.2s;
  &:hover {
    background: #1d4ed8;
    box-shadow: 0 6px 32px rgba(37,99,235,0.22);
  }

  @media (max-width: 600px) {
    max-width: 280px;
    font-size: 18px;
    padding: 16px 0;
    border-radius: 16px;
    gap: 8px;
  }
`;

const LoadingText = styled.div`
  text-align: center;
  color: ${props => props.theme.text.secondary};
  font-size: 16px;
  margin: 32px 0;
`;

export default CaseContentsView;